import React from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button, Field } from '../../cases/ui'

// ======================================================================
// THE STAGE EDITOR
// ======================================================================
//
// An ordered list of {key, label, target_days}. The order IS the
// configuration — er_cases/stages.parse_stages sorts nothing, because
// the array's order is what "next stage" and the progress rail mean —
// so reordering here is a real edit and not a display preference.
//
// ======================================================================
// THE KEY IS GENERATED ONCE AND NEVER EDITED. THIS IS THE WHOLE POINT.
// ======================================================================
//
// er_cases.current_stage stores the KEY. A case sitting on 'fact_find'
// is identified by that string and by nothing else: there is no stage
// id, no foreign key, and deliberately no CHECK constraint listing the
// legal values (migration 053, so a tenant names its own stages).
//
// So the two edits have completely different consequences:
//
//   RENAMING THE LABEL is free. 'fact_find' -> "Fact finding" changes
//   what everyone reads and nothing about what any case points at.
//   Every case on that stage keeps working.
//
//   CHANGING THE KEY ORPHANS LIVE CASES. Every case sitting on the old
//   key is now on a stage the new version does not contain. Task 22's
//   migration refuses to move those cases at all — they keep their old
//   version until they close — and their progress rail renders with no
//   position, because stage_index() has no answer for a key that is not
//   in the array.
//
// A UI that let somebody "fix a typo in the key" would therefore be
// offering a destructive act dressed as a correction. So the key is
// derived from the label at the moment a stage is CREATED, shown
// read-only so nobody is surprised by what the system is storing, and
// then frozen. Relabelling an existing stage never touches it.
//
// THIS IS NOT MERELY HIDING A FIELD. An existing stage's key is carried
// through this component untouched and handed straight back to the
// server; the only place a key is ever produced is _slug() below, and
// only for a row the user has just added.

const MAX_KEY = 40 // er_cases.current_stage is VARCHAR(40)
const MAX_LABEL = 80 // er_case_types stage label bound in parse_stages

/** The same shape er_cases/stages._SLUG accepts:
 *  ^[a-z0-9]+(?:_[a-z0-9]+)*$ — lowercase alphanumerics joined by single
 *  underscores.
 *
 *  DERIVED, THEN VALIDATED BY THE SERVER. This is a convenience so an
 *  administrator types a label rather than an identifier; it is not the
 *  authority on what a legal key is. parse_stages refuses anything this
 *  gets wrong, with a message naming the specific defect, and that
 *  message is shown verbatim. */
export function slugFromLabel(label, taken = []) {
  const base =
    String(label || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_{2,}/g, '_')
      .slice(0, MAX_KEY) || 'stage'

  if (!taken.includes(base)) return base
  // A duplicate key means a case sitting on it has no single position in
  // the order, which parse_stages refuses outright. Disambiguating here
  // is kinder than bouncing the whole form back for two stages a person
  // reasonably called the same thing.
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base.slice(0, MAX_KEY - 3)}_${n}`
    if (!taken.includes(candidate)) return candidate
  }
  return `${base.slice(0, MAX_KEY - 6)}_${Date.now() % 100000}`
}

export function newStage(label, existing) {
  return {
    key: slugFromLabel(label, existing.map((s) => s.key)),
    label,
    target_days: 5,
  }
}

// ======================================================================
// THE ORDERED LIST, EXTRACTED
// ======================================================================
//
// Lifted out of StageEditor unchanged in behaviour so the workflow
// builder (pages/admin/WorkflowEngineBuilder.jsx) can use the same
// list rather than growing a second one. DECISIONS01 §9 answer 5 said
// the builder canvas is "an ordered list, reusing StageEditor.jsx", and
// two editors that both reorder a keyed array would drift on the first
// bug fixed in one of them.
//
// WHAT IS ACTUALLY SHARED IS NOT THE MARKUP, IT IS THE DISCIPLINE. Both
// lists edit an array whose ORDER IS THE CONFIGURATION and whose items
// carry a KEY THAT SOMETHING ELSE HAS ALREADY STORED:
//
//   er_cases.current_stage             holds a stage key
//   workflow_step_executions.step_key  holds a step key
//   workflow_instances.current_step_keys holds step keys
//
// So the rule at the top of this file — derive the key from the label
// once, show it, never let it be edited — is not a cases-specific habit.
// A renamed step key is a drift record (A2) that stops matching the
// definition it was supposed to be evidence about, which is the same
// class of harm as a stranded case, reached by a different route.
//
// The caller supplies the row body; this owns the numbering, the
// reordering, the removal, the selection and the add control.
export function OrderedList({
  items,
  onChange,
  disabled,
  renderRow,
  labelOf = (it) => it.label || it.name || it.key,
  onAdd,
  addLabel = 'Add',
  header,
  hint,
  empty,
  selectedKey,
  onSelect,
  // (item, index) => 'error' | 'warn' | null. The builder anchors
  // validation findings with this: a rule that stopped a publish is
  // drawn on the row it is about, never in a toast that outlives the
  // reason for it.
  toneOf,
}) {
  const move = (i, delta) => {
    const j = i + delta
    if (j < 0 || j >= items.length) return
    const next = items.slice()
    const [row] = next.splice(i, 1)
    next.splice(j, 0, row)
    onChange(next)
  }

  const remove = (i) => onChange(items.filter((_, idx) => idx !== i))

  const TONE = {
    error: 'border-red-500/60 bg-red-500/5',
    warn: 'border-amber-500/50 bg-amber-500/5',
  }

  return (
    <div>
      {(header || hint) && (
        <div className="mb-2 flex items-baseline justify-between gap-3">
          {header && <span className="eyebrow">{header}</span>}
          {hint && <span className="text-[11.5px] text-faint">{hint}</span>}
        </div>
      )}

      {items.length === 0 ? (
        <p className="rounded-md border border-line bg-surface-2 px-4 py-5 text-center text-[12.5px] text-faint">
          {empty}
        </p>
      ) : (
        <ol className="space-y-2">
          {items.map((item, i) => {
            const tone = toneOf ? toneOf(item, i) : null
            const selected = selectedKey != null && item.key === selectedKey
            return (
              <li
                key={item.key || i}
                onClick={onSelect ? () => onSelect(item, i) : undefined}
                className={[
                  'rounded-md border px-3 py-3',
                  TONE[tone] || 'border-line bg-surface-2',
                  onSelect ? 'cursor-pointer' : '',
                  selected ? 'ring-1 ring-[var(--accent-line)]' : '',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  <span className="mono mt-2.5 w-5 shrink-0 text-center text-faint">
                    {i + 1}
                  </span>

                  <div className="min-w-0 flex-1 space-y-2">
                    {renderRow(item, i)}
                  </div>

                  <div
                    className="flex shrink-0 flex-col gap-1"
                    // The row is clickable in the builder; the controls
                    // inside it are not a selection gesture.
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={disabled || i === 0}
                      onClick={() => move(i, -1)}
                      aria-label={`Move ${labelOf(item)} earlier`}
                    >
                      <ArrowUp size={13} strokeWidth={1.9} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={disabled || i === items.length - 1}
                      onClick={() => move(i, 1)}
                      aria-label={`Move ${labelOf(item)} later`}
                    >
                      <ArrowDown size={13} strokeWidth={1.9} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={disabled}
                      onClick={() => remove(i)}
                      aria-label={`Remove ${labelOf(item)}`}
                    >
                      <Trash2 size={13} strokeWidth={1.9} />
                    </Button>
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      )}

      {onAdd && (
        <div className="mt-3">
          <Button size="sm" icon={Plus} disabled={disabled} onClick={onAdd}>
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  )
}

export default function StageEditor({ stages, onChange, disabled }) {
  const set = (i, patch) =>
    onChange(stages.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))

  return (
    <OrderedList
      items={stages}
      onChange={onChange}
      disabled={disabled}
      header="Stages, in order"
      hint="A case opens at the first stage and is moved through the rest by hand."
      empty="A case type needs at least one stage — the stage list is the procedure."
      addLabel="Add a stage"
      onAdd={() => onChange([...stages, newStage('New stage', stages)])}
      renderRow={(stage, i) => (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
            <input
              className="field"
              value={stage.label}
              maxLength={MAX_LABEL}
              disabled={disabled}
              aria-label={`Stage ${i + 1} name`}
              placeholder="Stage name, e.g. Investigation"
              onChange={(e) => set(i, { label: e.target.value })}
            />
            <div className="flex items-center gap-2">
              <input
                className="field w-20 text-right"
                type="number"
                min={1}
                max={3650}
                value={stage.target_days}
                disabled={disabled}
                aria-label={`Stage ${i + 1} target in working days`}
                onChange={(e) =>
                  set(i, {
                    // Kept as a number or as '' while the box is being
                    // retyped. The server rejects a non-integer with a
                    // worded message; sending NaN would produce a 422
                    // nobody wrote.
                    target_days:
                      e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
              />
              <span className="whitespace-nowrap text-[11.5px] text-faint">
                working days
              </span>
            </div>
          </div>

          {/* THE KEY, SHOWN AND NOT EDITABLE. See the block at the top of
              this file: a case stores this string, so changing it strands
              every case sitting on it. It is displayed rather than hidden
              entirely because an administrator debugging a report will
              meet it, and a value the system stores but never shows is
              worse than one shown as fixed. */}
          <p className="text-[11px] text-faint">
            Stored as <span className="mono text-muted">{stage.key}</span> —
            fixed once the stage exists, because cases record which stage they
            are on by this name. Renaming the stage above is safe and changes
            nothing for cases already running.
          </p>
        </>
      )}
    />
  )
}

/** The fields that are not stages: how long the whole thing should take,
 *  when to start chasing, who to escalate to, how long to keep the file.
 *
 *  SEPARATED FROM THE STAGE LIST because they behave differently on an
 *  edit. Changing any of these applies to every case that moves to the
 *  new version and strands none of them; changing the STAGES is the only
 *  thing that can leave a case behind. The migration prompt therefore
 *  only ever previews the stage array. */
export function TimingFields({ value, onChange, disabled }) {
  const set = (patch) => onChange({ ...value, ...patch })
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field
        label="Overall target"
        hint="Used only when a case is sitting on a stage its version no longer lists. Each stage has its own target above."
      >
        <input
          className="field"
          type="number"
          min={1}
          max={3650}
          disabled={disabled}
          value={value.target_days ?? ''}
          onChange={(e) =>
            set({ target_days: e.target.value === '' ? '' : Number(e.target.value) })
          }
        />
      </Field>

      <Field
        label="Start chasing after"
        hint="Working days into a stage before the case owner is reminded. Leave blank for no reminders."
      >
        <input
          className="field"
          type="number"
          min={1}
          max={3650}
          disabled={disabled}
          value={value.reminder_day ?? ''}
          placeholder="No reminders"
          onChange={(e) =>
            set({ reminder_day: e.target.value === '' ? null : Number(e.target.value) })
          }
        />
      </Field>

      <Field
        label="Escalate to"
        hint="A role ON THE CASE — case owner, investigator, decision maker, and so on. Never a platform role: somebody who is not a party to a case is never told it exists."
      >
        <input
          className="field"
          maxLength={40}
          disabled={disabled}
          value={value.escalate_to_role ?? ''}
          placeholder="case_owner"
          onChange={(e) =>
            set({ escalate_to_role: e.target.value === '' ? null : e.target.value })
          }
        />
      </Field>

      <Field
        label="Keep the file for"
        hint="Years after the case closes. The clock starts at closure, not at opening."
      >
        <input
          className="field"
          type="number"
          min={0}
          max={100}
          disabled={disabled}
          value={value.retention_years ?? ''}
          onChange={(e) =>
            set({
              retention_years: e.target.value === '' ? '' : Number(e.target.value),
            })
          }
        />
      </Field>
    </div>
  )
}
